import { decode } from 'base64-arraybuffer';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';

import { supabase } from '@/lib/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type ServiceRequestStatus = 'open' | 'closed' | 'cancelled';

export type ServiceRequest = {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  profession_category_id: number | null;
  location_id: number | null;
  status: ServiceRequestStatus;
  created_at: string;
  closed_at: string | null;
  target_professional_id: string | null;
  image_url: string | null;
  region: string | null;
};

const COLS = 'id, user_id, title, description, profession_category_id, location_id, status, created_at, closed_at, target_professional_id, image_url, region';

/** excludeUserId: αφαιρεί τα δικά μου αιτήματα από τον δημόσιο πίνακα — αν
 * κάποιος επαγγελματίας κάνει και ο ίδιος αίτημα ως πελάτης, δεν έχει νόημα
 * να βλέπει το δικό του αίτημα ανάμεσα σε αυτά που μπορεί να απαντήσει. */
export async function fetchOpenRequests(
  professionCategoryId?: number | number[] | null,
  excludeUserId?: string | null,
  region?: string | null,
): Promise<ServiceRequest[]> {
  let query = db.from('service_requests').select(COLS).eq('status', 'open').order('created_at', { ascending: false });
  if (Array.isArray(professionCategoryId)) {
    if (professionCategoryId.length > 0) query = query.in('profession_category_id', professionCategoryId);
  } else if (professionCategoryId) {
    query = query.eq('profession_category_id', professionCategoryId);
  }
  if (excludeUserId) query = query.neq('user_id', excludeUserId);
  // Αίτημα με region = null σημαίνει "Όλες οι περιοχές" — πρέπει να το βλέπει
  // κάθε επαγγελματίας, όχι μόνο όσους έχουν την ίδια περιοχή με τον αιτούντα.
  if (region) query = query.or(`region.eq.${region},region.is.null`);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as ServiceRequest[];
}

export async function countNewOpenRequests(sinceIso: string | null, excludeUserId?: string | null): Promise<number> {
  let query = db.from('service_requests').select('id', { count: 'exact', head: true }).eq('status', 'open');
  if (sinceIso) query = query.gt('created_at', sinceIso);
  if (excludeUserId) query = query.neq('user_id', excludeUserId);
  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
}

export async function fetchMyRequests(userId: string): Promise<ServiceRequest[]> {
  const { data, error } = await db
    .from('service_requests')
    .select(COLS)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ServiceRequest[];
}

/** Μόνο οι κατηγορίες επαγγέλματος που χρησιμοποιεί ήδη τουλάχιστον ένα ανοιχτό δημόσιο αίτημα. */
export async function listUsedRequestCategories(): Promise<{ id: number; name: string }[]> {
  const { data, error } = await db.rpc('list_used_request_categories');
  if (error) throw error;
  return (data ?? []) as { id: number; name: string }[];
}

/** Αριθμός δημόσιων ανοιχτών αιτημάτων ανά νομό — για το πράσινο "Ζητάνε" στον χάρτη. */
export async function getRequestCountsByRegion(professionCategoryId: number | null = null): Promise<Record<string, number>> {
  const { data, error } = await db.rpc('get_request_counts_by_region', { p_profession_category_id: professionCategoryId });
  if (error) throw error;
  const map: Record<string, number> = {};
  for (const row of (data ?? []) as { region: string; count: number }[]) map[row.region] = row.count;
  return map;
}

export async function fetchRequest(id: string): Promise<ServiceRequest | null> {
  const { data, error } = await db.from('service_requests').select(COLS).eq('id', id).maybeSingle();
  if (error) throw error;
  return data as ServiceRequest | null;
}

export async function createServiceRequest(input: {
  title: string; description?: string | null; professionCategoryId?: number | null; locationId?: number | null;
  /** Αν οριστεί, το αίτημα δεν δημοσιεύεται στον δημόσιο πίνακα — είναι ορατό μόνο σε αυτόν τον συγκεκριμένο επαγγελματία. */
  targetProfessionalId?: string | null;
  imageUrl?: string | null;
  region?: string | null;
}): Promise<string> {
  const { data, error } = await db.rpc('create_service_request', {
    p_title: input.title,
    p_description: input.description ?? null,
    p_profession_category_id: input.professionCategoryId ?? null,
    p_location_id: input.locationId ?? null,
    p_target_professional_id: input.targetProfessionalId ?? null,
    p_image_url: input.imageUrl ?? null,
    p_region: input.region ?? null,
  });
  if (error) throw error;
  return data as string;
}

/** Επιλέγει, συμπιέζει (JPEG, μεγάλη συμπίεση) και ανεβάζει μια φωτογραφία για
 * ένα αίτημα προσφοράς — μόνο για να πάρει μια ιδέα ο επαγγελματίας, όχι για
 * λεπτομέρεια, οπότε η ανάλυση περιορίζεται στα 800px στη μεγαλύτερη πλευρά.
 * Διαγράφεται αυτόματα (server-side trigger) μόλις το αίτημα κλείσει/ακυρωθεί. */
export async function pickAndUploadRequestImage(userId: string, source: 'library' | 'camera' = 'library'): Promise<string | null> {
  const result = source === 'camera'
    ? await (async () => {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (!perm.granted) throw new Error('Χρειάζεται άδεια πρόσβασης στην κάμερα.');
        return ImagePicker.launchCameraAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 1 });
      })()
    // requestMediaLibraryPermissionsAsync() ΔΕΝ χρειάζεται — system Photo Picker.
    : await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 1 });

  const asset = result.assets?.[0];
  if (result.canceled || !asset?.uri) return null;

  const longestSide = Math.max(asset.width ?? 800, asset.height ?? 800);
  const scale = Math.min(1, 800 / longestSide);
  const targetWidth = Math.round((asset.width ?? 800) * scale);
  const targetHeight = Math.round((asset.height ?? 800) * scale);

  const manipulated = await ImageManipulator.manipulateAsync(
    asset.uri,
    [{ resize: { width: targetWidth, height: targetHeight } }],
    { compress: 0.5, format: ImageManipulator.SaveFormat.JPEG, base64: true },
  );

  const path = `${userId}/${Date.now()}.jpg`;
  const { error: upErr } = await supabase.storage
    .from('request-images')
    .upload(path, decode(manipulated.base64!), { contentType: 'image/jpeg', cacheControl: '3600' });
  if (upErr) throw upErr;

  const { data } = supabase.storage.from('request-images').getPublicUrl(path);
  return data.publicUrl;
}

/** Επιτρέπεται μόνο όσο το αίτημα είναι ακόμα ανοιχτό (πριν δεχτεί/κλείσει με προσφορά). */
export async function updateServiceRequest(id: string, input: {
  title: string; description?: string | null; professionCategoryId?: number | null;
  imageUrl?: string | null; region?: string | null;
}): Promise<void> {
  const { error } = await db.from('service_requests').update({
    title: input.title,
    description: input.description ?? null,
    profession_category_id: input.professionCategoryId ?? null,
    image_url: input.imageUrl ?? null,
    region: input.region ?? null,
  }).eq('id', id);
  if (error) throw error;
}

export async function cancelServiceRequest(id: string): Promise<void> {
  const { error } = await db.from('service_requests').update({ status: 'cancelled' }).eq('id', id);
  if (error) throw error;
}

/** Αριθμός προσφορών ανά αίτημα, για τα δικά μου αιτήματα (badges στις κάρτες). */
export async function getOfferCountsForMyRequests(): Promise<Record<string, number>> {
  const { data, error } = await db.rpc('get_offer_counts_for_my_requests');
  if (error) throw error;
  const map: Record<string, number> = {};
  for (const row of (data ?? []) as { request_id: string; count: number }[]) map[row.request_id] = row.count;
  return map;
}

/** Σύνολο (νέων) προσφορών σε όλα τα δικά μου αιτήματα — για badges αρχικής/tab. */
export async function countOffersOnMyRequests(sinceIso: string | null): Promise<number> {
  const { data, error } = await db.rpc('count_offers_on_my_requests', { p_since: sinceIso });
  if (error) throw error;
  return (data as number) ?? 0;
}
